import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, shareReplay, tap } from 'rxjs';

import { SignOffEmployeesModule } from '@data-terminal/feature-dialogs/components/sign-off-employees/sign-off-employees.component';
import { SignOffOperationsModule } from '@data-terminal/feature-dialogs/components/sign-off-operations/sign-off-operations.component';
import { SignOffSummaryModule } from '@data-terminal/feature-dialogs/components/sign-off-summary/sign-off-summary.component';
import { SignOffTimeTransferComponent } from '@data-terminal/feature-dialogs/components/sign-off-time-transfer/sign-off-time-transfer.component';
import { SignOffWorkstationsModule } from '@data-terminal/feature-dialogs/components/sign-off-workstations/sign-off-workstations.component';
import { SignOffProcessService } from '@data-terminal/feature-dialogs/services/processes/sign-off-process.service';
import { GeneralActivitiesService } from '@data-terminal/feature-workstation-details/services/general-activities/general-activities.service';
import { SignOffDialogConfig, SignOffDialogStep } from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { Machine, SignOffMachine } from '@data-terminal/shared-models/machine';
import { Activity } from '@data-terminal/shared-models/operation';

import { AbstractSignOffDialogComponent } from '../abstract-sign-off-dialog.component';

type SignOffFormControls = {
    [SignOffDialogStep.WORKSTATIONS]: FormControl<boolean | null>;
    [SignOffDialogStep.OPERATIONS]: FormControl<boolean | null>;
    [SignOffDialogStep.EMPLOYEES]: FormControl<boolean | null>;
    [SignOffDialogStep.TIME_TRANSFER]: FormControl<boolean | null>;
    [SignOffDialogStep.SUMMARY]: FormControl<boolean | null>;
};

@Component({
    selector: 'data-terminal-sign-off-dialog',
    templateUrl: './sign-off-dialog.component.html',
    styleUrls: ['./sign-off-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [CdkStepper],
    imports: [
        MatStepperModule,
        MatToolbarModule,
        MatButtonModule,
        CommonModule,
        SignOffWorkstationsModule,
        SignOffEmployeesModule,
        SignOffOperationsModule,
        SignOffSummaryModule,
        SignOffTimeTransferComponent,
        TranslateModule,
        MatIconModule,
        CdkStepperModule,
    ],
})
export class SignOffDialogComponent extends AbstractSignOffDialogComponent<SignOffFormControls> {
    private readonly signOffProcessService = inject(SignOffProcessService);
    private readonly generalActivitiesService = inject(GeneralActivitiesService);

    public machinesToSignOff$: Observable<SignOffMachine[]>;
    public config$: Observable<SignOffDialogConfig>;
    public otherActivities$: Observable<Activity[]>;

    public dialogForm = new FormGroup<SignOffFormControls>({
        [SignOffDialogStep.WORKSTATIONS]: new FormControl<boolean | null>(null, Validators.required),
        [SignOffDialogStep.OPERATIONS]: new FormControl<boolean | null>(null, Validators.required),
        [SignOffDialogStep.EMPLOYEES]: new FormControl<boolean | null>(null, Validators.required),
        [SignOffDialogStep.TIME_TRANSFER]: new FormControl<boolean | null>(null, Validators.required),
        [SignOffDialogStep.SUMMARY]: new FormControl<boolean | null>(null),
    });

    protected readonly finalActionText = 'SIGN_OFF';

    constructor() {
        super();
        this.config$ = this.signOffProcessService.getConfig().pipe(tap((data) => this.setButtonsByConfig(data)));
        this.machinesToSignOff$ = this.signOffProcessService.getMachineToSignOff().pipe(shareReplay());
        this.otherActivities$ = this.generalActivitiesService.getUserActivities$();
    }

    public onEmployeesStateChange(signOffMachines: SignOffMachine[]): void {
        this.signOffProcessService.onEmployeesStateChange(signOffMachines);
    }

    public onOperationStateChange(signOffMachines: SignOffMachine[]): void {
        this.signOffProcessService.onOperationStateChange(signOffMachines);
    }

    public onWorkstationStateChange(machines: Machine[]): void {
        this.signOffProcessService.onWorkstationStateChange(machines);
    }

    protected override onTimeTransferDone(): void {
        const transferredMachineIds =
            this.timeTransferComponent?.signOffMachines().map((machine) => machine.machineId) || [];
        if (transferredMachineIds.length) {
            const machineIdSet = new Set(transferredMachineIds);
            this.unsubmittedTimeEntries = this.unsubmittedTimeEntries.filter(
                (entry) => !machineIdSet.has(entry.deviceId)
            );
            this.signOffProcessService.markSubmittedTimeEntriesForMachines(transferredMachineIds);
        }
    }
}
